import { Box, Fade, makeStyles, TextField, Typography } from '@material-ui/core';
import { useContext, useEffect, useState } from 'react';
import { transitionTimeout } from '../ClaimWizard';
import { separations } from '../../../../constants/StyleConstants';
import { Fragment } from 'react';
import { ClaimContext } from '../../../../contexts/ClaimContext';
import SimpleKeyValueText from '../../../../components/UI/SimpleKeyValueText';
import { dateToStr } from '../../../../utils/DateUtils';
import { Bold } from '../../../../components/UI/TextFormatters';
import { parseCLDR26 } from '../../../../utils/GridUtils';
import StCell from '../../../../components/StCell';
import { AppContext } from '../../../../contexts/AppContext';
import ClaimsAPI from '../../../../services/ClaimsAPI';
import clsx from 'clsx';

const useStyles = makeStyles(theme => ({
  ...separations,
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    '& .MuiOutlinedInput-multiline': {
      height: '100%',
      alignItems: 'flex-start',
    },
  },
  msgWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  msgBox: {
    backgroundColor: theme.palette.primary.light,
    borderRadius: 4,
    whiteSpace: 'nowrap',
  },
  descriptionInput: {
    height: '100%',
    margin: 0,
  },
}));

const BillInfo = ({ bill }) => (
  <div>
    <SimpleKeyValueText label="Fecha de factura" value={dateToStr(bill.invoiceDate)} />
    <SimpleKeyValueText label="Referencia" value={bill.reference} />
    <SimpleKeyValueText label="Doc. Compensación" value={bill.clearingDocument} />
    <SimpleKeyValueText label="Fecha de Vencimiento" value={dateToStr(bill.expirationDate)} />
    <SimpleKeyValueText label="Tipo documento" value={bill.docType} />
    <SimpleKeyValueText label="Importe" value={`${bill.currency} ${parseCLDR26(bill.import)}`} />
    <SimpleKeyValueText label="Fecha de pago" value={parseCLDR26(bill.paymentDate)} nullPlaceholder="-" />
    <SimpleKeyValueText label="Estado SAP" valueContent={<StCell value={bill.sapState === 'VERDE'} />} />
    <SimpleKeyValueText label="Estado" value={bill.state} nullPlaceholder="-" />
  </div>
);

const Message = ({ billClaim }) => {
  const classes = useStyles();

  switch (billClaim.action) {
    case 1:
      return (
        <Fragment>
          <div className={classes.msgWrapper}>
            <Box className={classes.msgBox} p={2}>
              <SimpleKeyValueText label="Orden de pago" value={billClaim.clearingDocument} />
              <SimpleKeyValueText label="Fecha de pago" value={dateToStr(billClaim.paymentDate)} />
            </Box>
          </div>
        </Fragment>
      );
    case 2:
      return (
        <Fragment>
          <div className={classes.msgWrapper}>
            <Box className={classes.msgBox} p={2}>
              <SimpleKeyValueText label="Fecha de vencimiento" value={dateToStr(billClaim.expirationDate)} />
            </Box>
          </div>
          <Typography align="center" className={classes.topSeparation} variant="subtitle2">
            <Bold>Se sugiere revisar el estado del reclamo en una fecha cercana a la del vencimiento</Bold>
          </Typography>
        </Fragment>
      );
    case 3:
      return (
        <Fragment>
          <div className={classes.msgWrapper}>
            <Box className={classes.msgBox} p={2}>
              <BillInfo bill={billClaim} />
            </Box>
          </div>
        </Fragment>
      );
    case 4:
      return (
        <Fragment>
          <div className={classes.msgWrapper}>
            <Box className={classes.msgBox} p={2}>
              <BillInfo bill={billClaim} />
            </Box>
          </div>
          <div className={classes.topSeparation}>
            <Typography align="center" variant="subtitle2">
              Se derivará la consulta a <Bold>mail@acerbrag.com</Bold>
            </Typography>
          </div>
        </Fragment>
      );
    case 5:
      return (
        <Fragment>
          <div className={classes.msgWrapper}>
            <Box className={classes.msgBox} p={2}>
              <BillInfo bill={billClaim} />
            </Box>
          </div>
        </Fragment>
      );

    default:
      break;
  }
};

const ActionMessage = ({ billClaim }) => {
  const classes = useStyles();
  const { showError, showWarning } = useContext(AppContext);
  const { setStepElements, setOpenBackDrop, selectedClaimId } = useContext(ClaimContext);
  const [transIn, setTransIn] = useState(true);
  const [description, setDescription] = useState('');

  const handleChangeDescription = value => {
    if (value.isBlank()) {
      setDescription('');
    } else {
      setDescription(value);
    }
  };

  useEffect(() => {
    const step = {
      stepNumber: 2,
      backButtonText: 'Cancelar y elegir otra factura',
      finishButtonText: 'Iniciar reclamo',
      finishButtonAction: sendClaim,
      transition: {
        enter: () => setTransIn(true),
        exit: () => setTransIn(false),
      },
    };

    setStepElements(step.stepNumber, step);
  }, [description]);

  const validate = () => {
    let errors = [];
    const errorMessages = [];
    let i = 0;

    if ((errors[i++] = description.isBlank())) errorMessages.push('Debe ingresar una descripción');

    const hasErrors = errors.some(e => e);

    hasErrors && showWarning(errorMessages);

    return !hasErrors;
  };

  const sendClaim = () => {
    return new Promise((resolve, reject) => {
      if (validate()) {
        setOpenBackDrop(true);

        const request = {
          idType: selectedClaimId,
          action: billClaim.action,
          description: description,
          additionalInfo: {
            reference: billClaim.reference,
            clearingDocument: billClaim.action === 1 ? billClaim.clearingDocument : undefined,
            area: billClaim.action === 4 ? billClaim.area : undefined,
          },
        };

        ClaimsAPI.startClaim(request)
          .then(res => {
            resolve();
          })
          .catch(error => {
            setOpenBackDrop(false);
            showError('Error al iniciar reclamo');
          });
      } else {
        reject();
      }
    });
  };

  return (
    <Fade in={transIn} timeout={transitionTimeout} unmountOnExit>
      <div className={classes.root}>
        <Typography className={classes.bottomSeparation} variant="subtitle2">
          Se iniciará el reclamo por la factura con los siguientes datos
        </Typography>
        <div className={clsx(classes.separationBetween)}>
          <Message billClaim={billClaim} />
          <TextField
            fullWidth
            className={classes.descriptionInput}
            margin="dense"
            id="description"
            multiline
            rows={5}
            variant="outlined"
            label="Descripción"
            onChange={e => handleChangeDescription(e.target.value)}
            value={description}
          />
        </div>
      </div>
    </Fade>
  );
};

export default ActionMessage;
